<template>
    <div class="ur-data-grid__header__cell" :style="thStyle" :class="thClass">
        <div class="ur-data-grid__header__resize-anchor" v-if="isResizable"
                @mousedown="pdn" @mouseup="pup" :style="resizeAnchor"/>
        <div class="ur-data-grid__header__cell__title" :style="titleStyle" @click="headerClick">
            <slot>
                <span class="ur-data-grid__header__cell__title__text" v-ur-tooltip.overflow="title"
                     :class="titleTextClass" >{{title}}</span>
                <span v-if="isHeaderIcon" class="ur-data-grid__header__cell__header-icon-wrapper" :class="headerIconClass">
                    <ur-icon v-if="isSortable" :icon="sortIcon" type="picto"
                        class="ur-data-grid__header__cell__sort-icon"
                        :class="sortIconClass"/>
                    <ur-icon v-if="isFilterable" icon="filter" type="picto" 
                        class="ur-data-grid__header__cell__filter-icon"
                        :class="filterIconClass"/>
                    <ur-icon v-if="isContext && !isSortable && !isFilterable" icon="repair" type="picto"
                        class="ur-data-grid__header__cell__context-icon"/>
                </span>
            </slot>
        </div>
    </div>
</template>


<script>
import {isNull, _rafThrottle} from "../util"
const MIN_WIDTH = 32;

export default {
    name : 'urDataGridHeaderCell',
    inject : ['gridInfo'],
    props : {
        idx     : {type:Number},
        field   : {type:Object},
        text    : {type:String},
        width   : {type:[Number,String]},
        colspan : {type:[Number,String]},
        rowspan : {type:[Number,String]},
        expandResizer : {type:[Number,String]},
        pseudo  : {type:Boolean},
        align  : {type:String}
    },
    computed : {
        // [COMPUTED][FIELD] Normalized field
        _field() {
            var rtn = {};
            if(this.field) {
                rtn = this.field;
            } else if(!isNull(this.idx)) {
                rtn = this.gridInfo.cols[this.idx];
            }
            return rtn;
        },
        // [COMPUTED][VALUE] colspan 값 변환
        _colspan()       {return parseInt(this.colspan)||1},
        // [COMPUTED][VALUE] rowspan 값 변환
        _rowspan()       {return parseInt(this.rowspan)||1},
        // [COMPUTED][VALUE] expandResizer 값 변환
        _expandResizer() {return parseInt(this.expandResizer)||1},
        // [COMPUTED][FLAG] 필수 항목입니까?
        isRequired()   {return this._field.required;},
        // [COMPUTED][FLAG] 여러 헤더가 머지된 상태입니까?
        isColspan() {return this._colspan > 1},
        // [COMPUTED][FLAG] 여러 헤더가 머지된 상태입니까?
        isRowspan() {return this._rowspan > 1},
        // [COMPUTED][FLAG] 동작 가능한 Cell입니까? (가짜 또는 가로합체셀 이 아닙니까?)
        isActionable() {return !this.pseudo && !this.isColspan},
        // [COMPUTED][FLAG] 헤더에 아이콘이 필요합니까?
        isHeaderIcon() {return this.isActionable && (this.isSortable || this.isFilterable || this.isContext)},
        // [COMPUTED][FLAG] 정렬 가능한 Field 입니까?
        isSortable()   {return this._field.__isSort;},
        // [COMPUTED][FLAG] 필터 가능한 Field 입니까?
        isFilterable() {return this._field.__isFilter;},
        // [COMPUTED][FLAG] 너비 조정이 가능합니까?
        isResizable() {
            return this.isActionable &&
                this.gridInfo.opts.resizableColumn &&
                this._field.resizable},
        // [COMPUTED][FLAG] 커스텀 컨텍스트가 있슴니까?
        isContext() {return this._field.headerContext},
        // [COMPUTED][FLAG] 정렬당하였습니까?
        isSorted() {return !!this._field.__sortOrder},
        // [COMPUTED][FLAG] 필터당하였습니까?
        isFiltered() {
            var fv = this._field.__filterValue
            if(isNull(fv)) return false;
            if(this._field.checker==='numberRange' && 
                    (!Array.isArray(fv) || fv.length !== 2 || (!fv[0] && !fv[1]))) {
                return false;
            }
            if(this._field.checker==='dateRange' &&
                    (!Array.isArray(fv) || fv.length !== 2 || (!fv[0] && !fv[1]))) {
                return false;
            }
            return true;
        },
        // [COMPUTED][FLAG] 정렬 아이콘을 보여주세요.
        sortIcon() {
            if(this._field.__sortOrder === 'A') return 'ascending';
            if(this._field.__sortOrder === 'D') return 'descending';
            return 'ascending';
        },
        // [COMPUTED][VIEW] Th cell 안에 보여질 타이틀
        title() {
            // 1. Text props, first
            if(this.text) {return this.text;}
            // 2. Default slot with simple text
            if(this.$slots.default) {
                var slotDefault = this.$slots.default;
                if(slotDefault.length === 1 && !slotDefault[0].tag && slotDefault[0].text) {
                    this.$slots.default = null;
                    return slotDefault[0].text;
                }
            }
            // 3. or Get title from field object
            if(!this._field){
                return '';
            } else if(this._field.text) {
                if(typeof this._field.text === 'function') {
                    return this._field.text();
                } else {
                    return this._field.text
                }
            } else {
                return this._field.value;
            }
        },
        // [COMPUTED][VIEW] Header cell의 스타일 입니다.
        /* NOSONAR */thStyle() {
            let style = {};
            // 1. calc width
            var dw = this.gridInfo.opts.defaultFieldWidth;
            // 1.1 check colspan & calc grow && basis
            var basisPer = 0, basisPx = 0, grow = 0;
            if(this._field.__idx === undefined) {
                // 1.2.1 pseudo column
                if(this.width) {
                    const WI = this.width;
                    if(typeof WI === 'string' && WI.slice(-1)==='%') {
                        basisPer = WI;
                    } else {
                        basisPx  = WI;
                    }
                } else {
                    grow++;
                }
            } else {
                // 1.2.2 maybe It's real
                var st = this._field.__idx, ed = st + this._colspan - 1;
                for(var i = st, ii=ed ; i<=ii ; i++ ) {
                    var col = this.gridInfo.cols[i],
                        wHide = col.hide,
                        wSelf = this.width || col.__width;
                    if(wHide) {
                        /*no op! hide*/
                    } else if(wSelf) {
                        if(typeof wSelf === 'string' && wSelf.slice(-1)==='%') {
                            basisPer += parseInt(wSelf.substr(0,wSelf.length-1));
                        } else {
                            basisPx += parseInt(wSelf);
                        }
                    } else { grow++; }
                }
            }
            
            // 1.2 set to style
            var basis = '', msBasis = '';
            if(basisPer && basisPx) {
                basis   = `calc(${basisPer}% + ${basisPx}px)`
                msBasis = `calc(${basisPer}% + ${basisPx + dw*grow}px)`
            } else if(basisPer) {
                basis   = `${basisPer}%`
                msBasis = `calc(${basisPer}% + ${dw*grow}px)`
            } else if(basisPx) {
                basis   = `${basisPx}px`
                msBasis = `${basisPx + dw*grow}px`
            } else {
                basis   = '0px'
                msBasis = '0px'
            }
            var isMinWidth = !basisPer&&!basisPx&&grow;
            style['flex']      = `${grow} ${grow} ${basis}`
            style['-ms-flex']  = `${grow} ${grow} ${msBasis}`
            style['min-width'] = `${isMinWidth?dw:1}px`
            if(!grow)style['max-width'] = basis;
            if(!grow&&!basisPer&&!basisPx) {
                style['opacity'] = 0;
                style['pointer-events'] = 'none';
            }
            // 2. set height
            var h = this.gridInfo.opts.headerHeight;
            if(this.isRowspan) {
                style['height']        = `${ h*this._rowspan}px`;
                style['margin-bottom'] = `${-h*(this._rowspan-1)}px`;
            } else {
                style['height'] = `${h}px`;
            }
            return style;
        },
        // [COMPUTED][VIEW] Header cell의 클래스 입니다.
        thClass() {
            let clazz = [];
            if(this._field.type === 'number') {
                clazz.push('ur-data-grid__header__cell--number')
            } else if(this._field.type === 'code') {
                clazz.push('ur-data-grid__header__cell--code')
            }
            if( this.isActionable && (this.isSortable || this.isFilterable || this.isContext)) {
                clazz.push('ur-data-grid__header__cell--actionable')
            }
            if(this.pseudo)  { clazz.push('ur-data-grid__header__cell--pseudo') }
            if(this.colspan) { clazz.push('ur-data-grid__header__cell--colspan') }
            return clazz;
        },
        // [COMPUTED][VIEW] Title 영역의 class 입니다.
        titleStyle() {
            let align = this.align || this._field.align

            switch(align) {
                case 'left'  :return {'justify-content': 'flex-start'};
                case 'right' :return {'justify-content': 'flex-end'};
                case 'center':return {'justify-content': 'center'};
            }
            // if(this.isColspan) {
            //     return {'justify-content':'flex-start'}
            // } else if(this._field.align) {
            //     switch(this._field.align) {
            //     case 'left'  : return {'justify-content':'flex-start'};
            //     case 'right' : return {'justify-content':'flex-end'};
            //     case 'center': return {'justify-content':'center'};
            //     }
            // }
            return null;
        },
        titleTextClass() {
           return {'ur-data-grid__header__cell__title__text--required':this.isRequired}
        },
        // [COMPUTED][VIEW] Resize 막대기의 높이를 지정합니다.
        resizeAnchor() {
            if(this._expandResizer>1) { 
                // 1. props:expandResizer가 지정이 되어있는 경우
                return {
                    'top'   :`${-this.gridInfo.opts.headerHeight * (this._expandResizer-this._rowspan)}px`,
                    'height':`${ this.gridInfo.opts.headerHeight * this._expandResizer}px`
                };
            } else {
                // 2. rowspan만 보고 하기, (normalized... rowspan 안너면 1)
                return {
                    'height':`${ this.gridInfo.opts.headerHeight * this._rowspan}px`
                };
            }
            
        },
        // [COMPUTED][VIEW] Icon의 class를 지정합니다.      => 같이 또는 홀로
        headerIconClass(){ return {'ur-data-grid__header__cell__header-icon--both' : this.isSortable && this.isFilterable} },
        // [COMPUTED][VIEW] 정렬 Icon의 class를 지정합니다. => 활성화 여부
        sortIconClass()  { return {'ur-data-grid__header__cell__header-icon--active':this.isSorted} },
        // [COMPUTED][VIEW] 필터 Icon의 class를 지정합니다. => 활성화 여부
        filterIconClass(){ return {'ur-data-grid__header__cell__header-icon--active':this.isFiltered} },
    },
    methods : {
        headerClick(e) {
            if(this.pseudo || this.isColspan) {return;}
            // 1. check exist
            var hCtx = this.gridInfo.vars_headerContext;
            if(hCtx) {
                this.gridInfo.func.setHeaderContext(null);
                // 나를 다시 눌러서 또 여는건 금지
                if(hCtx === this._field) {return;}
            }
            // 2. open or not
            if(this.isFilterable || this.isContext) {// include sortable...
                // 2.1 Calc position
                var el=this.$el        , elRect = el.getBoundingClientRect();
                var pl=this.$parent.$el, plRect = pl.getBoundingClientRect();
                // 2.1.1 calc position 
                var pos = elRect.left - plRect.left;
                if(pos < 0) pos = 0;
                // 2.1.2 If scrollwrapper? Append freeze width
                // 야매 : freeze 빼고, scroll쪽에는 freezeWidth만큼 밀어야 함
                //        -> margin-left가 있는 경우 마진된만큼 밀어야 햠
                if(pl.style['margin-left']) {
                    pos += this.gridInfo.vars_freezeWidth;
                }
                // 2.2 set to field (temp attr __posLeft)
                this._field.__posLeft = pos;
                // 2.3 open context
                this.gridInfo.func.setHeaderContext(this._field);
            } else if(this.isSortable) {
                this.gridInfo.func.toggleSortField(this._field);
            }
        },
        // [RESIZE] Start resize
        pdn(e) {
            e.preventDefault();
            e.stopPropagation();
            // 1. Set offset : cell.right - click pos
            var el = this.$el, elRect = el.getBoundingClientRect();
            this._offset = elRect.width - (e.clientX - elRect.left);
            // 2. set pointerId & move handler
            // var tl = e.target;
            // tl.setPointerCapture(e.pointerId)
            // tl.onmousemove = this.pmv;
            //fixme safari 12를 포기합시다
            document.body.addEventListener('mousemove', this.pmv);
            document.body.addEventListener('mouseup', this.pup);
        },
        // [RESIZE] On resize
        pmv: _rafThrottle(function(e) {
            // 1. Calc pos
            var el = this.$el        , elRect = el.getBoundingClientRect();
            var pl = this.$parent.$el, plRect = pl.getBoundingClientRect();
            var pos = e.clientX - plRect.left;
            if(e.clientX < elRect.left + MIN_WIDTH) {pos = elRect.left - plRect.left + MIN_WIDTH}
            // 2. add scroll when left-margin?
            // 야매? : freeze 빼고, scroll쪽에는 freezeWidth만큼 밀어야 함
            //         -> margin-left가 있는 경우 마진된만큼 밀어야 햠
            //         -> parent의 parent.left랑 하면 될까나? -> Header > row > cell
            if(pl.style['margin-left']) {pos += this.gridInfo.vars_freezeWidth;}
            // 3. Move guide
            if(this._offset >= 0){
                this.gridInfo.func.resizerPos(pos);
            }
        }),
        // [RESIZE] End resize
        pup(e) {
            e.preventDefault();
            e.stopPropagation();
            // var tl = e.target;
            var el = this.$el, elRect = el.getBoundingClientRect();
            // tl.releasePointerCapture(e.pointerId)
            // tl.onmousemove = null;
            //fixme safari 12를 포기합시다
            document.body.removeEventListener('mousemove', this.pmv);
            document.body.removeEventListener('mouseup', this.pup);
            // 1. Reset guide
            this.gridInfo.func.resizerPos(0);
            // 2. set width
            // _offset, 헤더 끝쪽을 정확히 안찝으면 offset
            var pos = e.clientX - elRect.left + this._offset;
            this._offset = -1;
            // MIN_WIDTH : 이거 이하로는 못줄임
            if( pos < MIN_WIDTH) pos = MIN_WIDTH
            this.gridInfo.func.setFieldWidth(this._field, pos);
        },
    }
}

</script>